export type HudMode = "hidden" | "countdown" | "timer" | "crash" | "finish";

export class Hud {
  private root: HTMLDivElement;
  private countdownEl: HTMLDivElement;
  private timerEl: HTMLDivElement;
  private centerEl: HTMLDivElement;
  private tapHintEl: HTMLDivElement | null = null;

  constructor(showTapHint: boolean) {
    const hud = document.getElementById("hud");
    if (!hud) {
      throw new Error("#hud not found");
    }
    this.root = hud as HTMLDivElement;

    this.countdownEl = document.createElement("div");
    Object.assign(this.countdownEl.style, {
      position: "absolute",
      top: "0",
      left: "0",
      right: "0",
      display: "none",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      color: "white",
      fontSize: "min(28vh, 240px)",
      fontWeight: "800",
      letterSpacing: "-0.02em",
      textShadow: "0 6px 24px rgba(0,0,0,0.6)",
      pointerEvents: "none"
    } as Partial<CSSStyleDeclaration>);
    this.root.appendChild(this.countdownEl);

    this.timerEl = document.createElement("div");
    Object.assign(this.timerEl.style, {
      position: "absolute",
      top: "16px",
      right: "20px",
      display: "none",
      color: "white",
      fontSize: "32px",
      fontWeight: "700",
      letterSpacing: "0.02em",
      textShadow: "0 2px 8px rgba(0,0,0,0.6)",
      pointerEvents: "none",
      fontVariantNumeric: "tabular-nums"
    } as Partial<CSSStyleDeclaration>);
    this.root.appendChild(this.timerEl);

    this.centerEl = document.createElement("div");
    Object.assign(this.centerEl.style, {
      position: "absolute",
      inset: "0",
      display: "none",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: "12px",
      color: "white",
      fontFamily: "system-ui, -apple-system, sans-serif",
      textShadow: "0 4px 16px rgba(0,0,0,0.7)",
      pointerEvents: "none",
      background: "rgba(8, 14, 30, 0.5)"
    } as Partial<CSSStyleDeclaration>);
    this.root.appendChild(this.centerEl);

    if (showTapHint) {
      this.tapHintEl = document.createElement("div");
      Object.assign(this.tapHintEl.style, {
        position: "absolute",
        inset: "0",
        display: "flex",
        pointerEvents: "none"
      } as Partial<CSSStyleDeclaration>);
      this.tapHintEl.innerHTML = `
        <div style="flex:1;border-right:1px dashed rgba(255,255,255,0.15);display:flex;align-items:flex-end;justify-content:center;padding-bottom:24px;color:rgba(255,255,255,0.55);font:600 14px system-ui;text-shadow:0 1px 4px rgba(0,0,0,0.6);">TAP ◀</div>
        <div style="flex:1;display:flex;align-items:flex-end;justify-content:center;padding-bottom:24px;color:rgba(255,255,255,0.55);font:600 14px system-ui;text-shadow:0 1px 4px rgba(0,0,0,0.6);">▶ TAP</div>
      `;
      this.root.appendChild(this.tapHintEl);
    }
  }

  private hideAll(): void {
    this.countdownEl.style.display = "none";
    this.timerEl.style.display = "none";
    this.centerEl.style.display = "none";
  }

  showCountdown(elapsed: number): void {
    this.hideAll();
    const remaining = 3 - elapsed;
    let text: string;
    if (remaining > 2) text = "3";
    else if (remaining > 1) text = "2";
    else if (remaining > 0) text = "1";
    else text = "GO!";
    this.countdownEl.textContent = text;
    this.countdownEl.style.display = "flex";
  }

  showTimer(seconds: number): void {
    this.hideAll();
    this.timerEl.textContent = formatTime(seconds);
    this.timerEl.style.display = "block";
  }

  showCrash(): void {
    this.hideAll();
    this.centerEl.innerHTML = `
      <div style="font-size:48px;font-weight:800;">CRASH!</div>
      <div style="font-size:18px;opacity:0.85;">resetting…</div>
    `;
    this.centerEl.style.display = "flex";
  }

  showFinish(seconds: number, restartLabel: string): void {
    this.hideAll();
    this.centerEl.innerHTML = `
      <div style="font-size:56px;font-weight:800;letter-spacing:0.02em;">FINISH!</div>
      <div style="font-size:64px;font-weight:700;font-variant-numeric:tabular-nums;">${formatTime(seconds)}</div>
      <div style="font-size:18px;opacity:0.9;margin-top:8px;">${restartLabel}</div>
    `;
    this.centerEl.style.display = "flex";
  }

  dispose(): void {
    this.countdownEl.remove();
    this.timerEl.remove();
    this.centerEl.remove();
    if (this.tapHintEl) this.tapHintEl.remove();
  }
}

function formatTime(seconds: number): string {
  const total = Math.max(0, seconds);
  const m = Math.floor(total / 60);
  const s = Math.floor(total % 60);
  const cs = Math.floor((total - Math.floor(total)) * 100);
  return `${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}.${cs.toString().padStart(2, "0")}`;
}
